import { apiFetch } from "./api";
import type { PagedResult } from "./clients-accounts";

// ============================================================================
// DTOs (mirror src/Allmark.Contracts/Enquiries/*.cs)
//
// Wire-format casing: ASP.NET Core System.Text.Json default = camelCase.
// ============================================================================

export type EnquiryStatus = "New" | "InQuote" | "Quoted" | "Won" | "Lost" | "Abandoned";

// ----------------------------------------------------------------------------
// Enquiry list / detail
// ----------------------------------------------------------------------------

export interface EnquiryListItemDto {
  enquiryId: number;
  clientId: number | null;
  clientName: string | null;
  ownerEmployeeId: number | null;
  ownerEmployeeName: string | null;
  branchId: number | null;
  description: string | null;
  status: EnquiryStatus;
  sourceSystem: string;
  externalId: string | null;
  createdOn: string | null;
}

export interface EnquiryDetailDto {
  enquiryId: number;
  clientId: number | null;
  clientName: string | null;
  ownerEmployeeId: number | null;
  ownerEmployeeName: string | null;
  branchId: number | null;
  description: string | null;
  status: EnquiryStatus;
  sourceSystem: string;
  externalId: string | null;
  createdOn: string | null;
  rowVersionBase64: string | null;
}

// ----------------------------------------------------------------------------
// Enquiry items
// ----------------------------------------------------------------------------

export interface EnquiryItemDto {
  enquiryItemId: number;
  enquiryId: number;
  sortOrder: number;
  description: string;
  quantity: number | null;
  notes: string | null;
  rowVersionBase64: string | null;
}

export interface CreateEnquiryItemRequest {
  sortOrder?: number | null;
  description: string;
  quantity?: number | null;
  notes?: string | null;
}

export interface UpdateEnquiryItemRequest {
  sortOrder: number;
  description: string;
  quantity: number | null;
  notes: string | null;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Enquiry create / update
// ----------------------------------------------------------------------------

export interface CreateEnquiryRequest {
  clientId?: number | null;
  ownerEmployeeId?: number | null;
  branchId?: number | null;
  description?: string | null;
  sourceSystem?: string;
  externalId?: string | null;
}

export interface UpdateEnquiryRequest {
  clientId?: number | null;
  ownerEmployeeId?: number | null;
  branchId?: number | null;
  description?: string | null;
  rowVersionBase64: string;
}

// ----------------------------------------------------------------------------
// Convert to quote
// ----------------------------------------------------------------------------

export interface ConvertEnquiryToQuoteResult {
  quoteId: number;
  quoteRef: string;
}

// ----------------------------------------------------------------------------
// List query params
// ----------------------------------------------------------------------------

export interface ListEnquiriesParams {
  status?: EnquiryStatus | EnquiryStatus[];
  clientId?: number;
  ownerEmployeeId?: number;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// Convenience type aliases used by SPA components
// ============================================================================

export type EnquiryListItem = EnquiryListItemDto;
export type EnquiryDetail = EnquiryDetailDto;
export type EnquiryItem = EnquiryItemDto;

// ============================================================================
// Query-string helper
// ============================================================================

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, String(item));
    } else {
      sp.append(k, String(v));
    }
  }
  return sp.toString();
}

// ============================================================================
// API helpers
// ============================================================================

export const enquiriesApi = {
  // ---- Enquiries --------------------------------------------------------------

  list: (params: ListEnquiriesParams = {}): Promise<PagedResult<EnquiryListItemDto>> => {
    const search = qs(params as Record<string, unknown>);
    return apiFetch<PagedResult<EnquiryListItemDto>>(`/api/enquiries${search ? `?${search}` : ""}`);
  },

  get: (id: number): Promise<EnquiryDetailDto> =>
    apiFetch<EnquiryDetailDto>(`/api/enquiries/${id}`),

  create: (body: CreateEnquiryRequest): Promise<EnquiryDetailDto> =>
    apiFetch<EnquiryDetailDto>("/api/enquiries", { method: "POST", body: JSON.stringify(body) }),

  patch: (id: number, body: UpdateEnquiryRequest): Promise<void> =>
    apiFetch<void>(`/api/enquiries/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  delete: (id: number): Promise<void> =>
    apiFetch<void>(`/api/enquiries/${id}`, { method: "DELETE" }),

  // ---- Enquiry items ----------------------------------------------------------

  items: (id: number): Promise<EnquiryItemDto[]> =>
    apiFetch<EnquiryItemDto[]>(`/api/enquiries/${id}/items`),

  addItem: (id: number, body: CreateEnquiryItemRequest): Promise<EnquiryItemDto> =>
    apiFetch<EnquiryItemDto>(`/api/enquiries/${id}/items`, { method: "POST", body: JSON.stringify(body) }),

  patchItem: (id: number, itemId: number, body: UpdateEnquiryItemRequest): Promise<void> =>
    apiFetch<void>(`/api/enquiries/${id}/items/${itemId}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteItem: (id: number, itemId: number): Promise<void> =>
    apiFetch<void>(`/api/enquiries/${id}/items/${itemId}`, { method: "DELETE" }),

  // ---- Convert to quote -------------------------------------------------------

  convertToQuote: (id: number): Promise<ConvertEnquiryToQuoteResult> =>
    apiFetch<ConvertEnquiryToQuoteResult>(`/api/enquiries/${id}/convert-to-quote`, { method: "POST" }),
};
